import React, { useEffect, useState, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Container from 'reactstrap/lib/Container';
import classnames from 'classnames';
import { get, isEqual } from 'lodash-es';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';

/* Models */
import { bindToContent, CmsEntities } from 'client/data/models/cms';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { ComparatorModel } from 'client/data/models/comparator';
import { convertSubmodelStyleToStyle, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';

/* Utils */
import { TrackingHandler } from 'client/tracking/handler';
import { parseVehicleParam } from 'client/utils/parse-vehicle-param';
import { getQuery } from 'client/utils/location';
import { venomHistory } from 'client/utils/history/venom-history';
import { queryToSearch } from 'client/utils/query-to-search';
import { isUsedState } from 'site-modules/shared/utils/publication-states';
import { firstVehicle, normalizeQuery } from 'site-modules/comparator/utils/query-handler';
import { buildVehiclePath, getColumnIdsString } from 'site-modules/comparator/utils/comparator';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';

/* Components */
import { AnchorSubNavigation } from 'site-modules/shared/components/anchor-sub-navigation/anchor-sub-navigation';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';
import { PricingCarouselFooter } from 'site-modules/shared/components/core-page/pricing-carousel/pricing-carousel-footer';
import { Faq } from 'site-modules/shared/components/faq/faq';
import { ZipWithDefaultActionHandling } from 'site-modules/shared/components/zip-with-default-action-handling/zip-with-default-action-handling';
import { ComparatorGrid } from 'site-modules/comparator/components/comparator-grid/comparator-grid';
import { ComparatorInfoSection } from 'site-modules/comparator/components/comparator-info-section/comparator-info-section';
import { SuggestedLinks } from 'site-modules/comparator/components/suggested-links/suggested-links';
import { CustomHeroWrapper } from 'site-modules/shared/components/custom-hero-wrapper/custom-hero-wrapper';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { HideSimilaritiesSwitch } from 'site-modules/comparator/components/hide-similarities-switch/hide-similarities-switch';
import { CarNewsMarquee } from 'site-modules/shared/components/upper-funnel/home-marquee/car-news-marquee';
import { VehicleShowcase } from 'site-modules/shared/components/upper-funnel/vehicle-showcase/vehicle-showcase';
import { HomeModulesModel } from 'client/data/models/home-modules';

/* Constants */
import { ComparatorMap } from 'client/tracking/maps/comparator/comparator';
import { HomepageAdExtensionMap } from 'client/tracking/maps/common/homepage-ad-extension';
import { DEFAULT_CONTENT, transform } from 'client/data/cms/content';
import { SUB_NAV_SECTIONS_LIST } from 'site-modules/comparator/constants/sections-map';
import { COMPARATOR_FAQ_CONTENT } from 'site-modules/comparator/constants/faq-content';
import { MM_VS_MM_SUMMARY } from 'client/site-modules/comparator/constants/page-summary';
import {
  COMPARATOR_PATHNAME,
  DESKTOP_DEVICE,
  MAX_COMPARE_SIZE,
  MOBILE_DEVICE,
  VEHICLE_PATH_NAME,
} from 'site-modules/comparator/constants/comparator';
import { useDebounce } from 'site-modules/shared/hooks/use-debounce';

import './comparator.scss';

export function ComparatorUI({
  location = {
    search: '',
  },
  visitorLocation,
  isMobile = false,
  sponsorship = undefined,
  veh1 = null,
  veh2 = null,
  veh3 = null,
  veh4 = null,
  isMMvsMMComparator = false,
  hasEvVehicle = false,
  isCore6982Enabled = false,
  vehicleShowcaseContent = DEFAULT_CONTENT,
}) {
  const [hideSimilar, toggleHideSimilar] = useToggle(false);
  const [customOffset, setCustomOffset] = useState(0);

  const adjustCustomOffset = useDebounce(newCustomOffset => setCustomOffset(newCustomOffset), [], 500);

  useEffect(() => {
    const { pathname } = location;
    const query = getQuery(location);

    const newState = normalizeQuery(query, pathname, MAX_COMPARE_SIZE, !!sponsorship);

    if (!isEqual(newState.query, query)) {
      venomHistory.replace({ ...newState, search: queryToSearch(newState.query), query: undefined });
    }
    TrackingHandler.useMap({ ...ComparatorMap, ...HomepageAdExtensionMap });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navList = SUB_NAV_SECTIONS_LIST.filter(({ isEvItem }) => !isEvItem || hasEvVehicle);
  const { city, stateCode } = visitorLocation;
  const query = getQuery(location);
  const pathname = get(location, 'pathname');

  const columns = new Array(MAX_COMPARE_SIZE).fill().map((item, index) => {
    const name = `${VEHICLE_PATH_NAME}${index + 1}`;
    return isMMvsMMComparator
      ? { name, styleId: get([veh1, veh2, veh3, veh4][index], 'id', '').toString() }
      : parseVehicleParam(query, name);
  });

  const pricingVehicle = [veh1, veh2, veh3, veh4].find(veh => !!veh && !isUsedState(get(veh1, 'publicationState', '')));

  const breadcrumbs = [
    { title: 'Home', href: '/' },
    { title: 'Compare cars', href: COMPARATOR_PATHNAME },
  ];

  const makeModelVsMakeModelTitle = [veh1, veh2, veh3, veh4]
    .filter(Boolean)
    .map(({ makeName, modelName }) => `${makeName} ${modelName}`)
    .join(' vs ');

  if (isMMvsMMComparator && makeModelVsMakeModelTitle) {
    breadcrumbs.push({
      title: makeModelVsMakeModelTitle,
      href: pathname,
    });
  }

  const title =
    isMMvsMMComparator && makeModelVsMakeModelTitle ? makeModelVsMakeModelTitle : 'Compare Cars Side-by-Side';

  const pageSummary = MM_VS_MM_SUMMARY[pathname];

  const hasComparisonContent = !!(veh1 || veh2 || veh3 || veh4);
  const renderMarqueeHvs = (
    <Fragment>
      <Container>
        <hr className="mt-1_5 mb-2" />
        <CarNewsMarquee className="mt-3_5 mb-3_5" scrollBtnPosition={isMobile ? 'start' : 'end'} />
      </Container>
      <MinHeightWrapper componentType="HVS_MODULE">
        <VehicleShowcase
          content={vehicleShowcaseContent}
          isMediumLayout={false}
          useContainer
          className="mb-2"
          bottomBorder={false}
        />
      </MinHeightWrapper>
    </Fragment>
  );

  return (
    <main id="main-content" tabIndex={-1} className="comparator mb-3" data-tracking-parent="comparator">
      <Breadcrumbs className="ps-0_5 mb-0 container" data={breadcrumbs} aria-label="Breadcrumbs" />

      <CustomHeroWrapper>
        {isMobile ? (
          <Container className="px-xl-0_25">
            <h1 className="heading-2 pt-1 px-xl-0_25 mb-0 text-start">{title}</h1>
          </Container>
        ) : (
          <div className="sticky-nav-container">
            <Container className="px-xl-0_25">
              <h1 className="heading-2 pt-1 px-xl-0_25 mb-0 text-start">{title}</h1>
              {hasComparisonContent && (
                <AnchorSubNavigation customOffset={customOffset} list={navList} hasBubblesDesign />
              )}
            </Container>
          </div>
        )}
      </CustomHeroWrapper>
      <Container className="text-start mb-1_25 mt-1">
        <div className="me-0_25 d-inline-block">Comparing average pricing near</div>
        <div className="d-inline-block">
          {city}, {stateCode} <ZipWithDefaultActionHandling buttonClassName="size-16 fw-normal" />
        </div>
        {Boolean(pageSummary) && (
          <section className="my-1">
            <h2 className="heading-5">How They Compare</h2>
            <ContentCollapse
              showBtnBelow
              minHeight={'44px'} // line height of 2 lines
              justify="justify-content-start"
              btnContainerClasses="m-0 px-0"
              classes="size-16 mt-0_5"
              btnClasses="mt-0_5 p-0 size-16 text-primary-darker"
              isEclipseFade={false}
            >
              <p className={classnames('size-16 mb-0 page-summary-text')}>{pageSummary}</p>
            </ContentCollapse>
          </section>
        )}
        <HideSimilaritiesSwitch className="mt-1" hideSimilar={hideSimilar} toggleHideSimilar={toggleHideSimilar} />
      </Container>

      <ComparatorGrid
        title={title}
        sponsoredContent={sponsorship}
        columns={columns}
        navList={navList}
        location={location}
        isMobile={isMobile}
        setCustomOffset={adjustCustomOffset}
        customOffset={customOffset}
        deviceName={isMobile ? MOBILE_DEVICE : DESKTOP_DEVICE}
        hideSimilar={hideSimilar}
        isMMvsMMComparator={isMMvsMMComparator}
        hasComparisonContent={hasComparisonContent}
        isCore6982Enabled={isCore6982Enabled}
      />

      <SuggestedLinks isMobile={isMobile} />
      {!isMMvsMMComparator && (
        <Container className="mt-2">
          <ComparatorInfoSection />
        </Container>
      )}

      {pricingVehicle && (
        <Fragment>
          <Container>
            <MinHeightWrapper
              className="suggested-pricing-wrapper show-separator"
              componentType="SUGGESTED_PRICING_CAROUSEL"
            >
              <PricingCarouselFooter
                params={{
                  make: pricingVehicle.makeSlug,
                  model: pricingVehicle.modelSlug,
                  year: pricingVehicle.year,
                }}
                srpParams={{
                  make: pricingVehicle.makeSlug,
                  model: pricingVehicle.modelSlug,
                  year: pricingVehicle.year,
                  inventorytype: 'new',
                }}
                className="mt-1"
              />
            </MinHeightWrapper>
          </Container>
          {renderMarqueeHvs}
        </Fragment>
      )}

      {!isMMvsMMComparator && (
        <Container>
          <hr className="mt-1_5 mb-2" />
          <Faq
            entries={COMPARATOR_FAQ_CONTENT}
            learnMoreLinkText="Compare Cars Here"
            learnMoreLinkClassName="fw-bold d-block mt-0_5"
            pathname={COMPARATOR_PATHNAME}
          />
        </Container>
      )}
      {!pricingVehicle && renderMarqueeHvs}

      <Container>
        <PageLinksWidget divideSections={false} title="Related information" hasTopDivider />
      </Container>
    </main>
  );
}

ComparatorUI.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
  visitorLocation: PropTypes.shape({
    stateCode: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
  }).isRequired,
  isMobile: PropTypes.bool,
  sponsorship: PropTypes.shape({}),
  veh1: VehicleEntities.Style,
  veh2: VehicleEntities.Style,
  veh3: VehicleEntities.Style,
  veh4: VehicleEntities.Style,
  isMMvsMMComparator: PropTypes.bool,
  hasEvVehicle: PropTypes.bool,
  isCore6982Enabled: PropTypes.bool,
  vehicleShowcaseContent: CmsEntities.Content,
};

export const mapStateToProps = state => ({
  isMobile: get(state, 'mobile'),
  visitorLocation: get(state, 'visitor.location'),
  location: get(state, 'pageContext.location'),
  isCore6982Enabled: ExperimentUtil.getForcedOrAssignedRecipeName({
    state,
    campaignName: 'core-6982-comparator-leads',
    defaultVal: 'ctrl',
  }).includes('chal'),
});

const ComparatorWrapper = connectToModel(ComparatorUI, {
  hasEvVehicle: bindToPath(
    ({ veh1, veh2, veh3, veh4 }) =>
      `evEfficiency["${getColumnIdsString([veh1, veh2, veh3, veh4].map(veh => ({ styleId: get(veh, 'id') })))}"]`,
    ComparatorModel,
    evComparableFeatures =>
      evComparableFeatures.some(
        evComparableFeature => get(evComparableFeature, 'isElectric') || get(evComparableFeature, 'isPluginElectric')
      )
  ),
});

export const stateToPropsConfig = {
  sponsorship: bindToPath(({ location }) => {
    const query = getQuery(location);
    const vehicle = firstVehicle(query, MAX_COMPARE_SIZE);
    if (!vehicle || !vehicle.styleId) {
      return 'sponsorship.empty';
    }
    return `sponsorship.byStyle["${vehicle.styleId}"]`;
  }, ComparatorModel),
  content: bindToContent('comparator/compare-venom'),
  veh1: bindToPath(({ location }) => buildVehiclePath(location, 1), VehicleModel, convertSubmodelStyleToStyle),
  veh2: bindToPath(({ location }) => buildVehiclePath(location, 2), VehicleModel, convertSubmodelStyleToStyle),
  veh3: bindToPath(({ location }) => buildVehiclePath(location, 3), VehicleModel, convertSubmodelStyleToStyle),
  veh4: bindToPath(({ location }) => buildVehiclePath(location, 4), VehicleModel, convertSubmodelStyleToStyle),
  vehicleShowcaseContent: bindToPath('vehicleShowcaseContent', HomeModulesModel, transform),
};

export const Comparator = connect(mapStateToProps)(connectToModel(ComparatorWrapper, stateToPropsConfig));
